import { ReactElement, useEffect, useMemo, useState } from "react"
import Select from "react-select"
import { DateRangePicker } from "react-dates"
import { AutoSizer } from "react-virtualized"
import BaseTable, { Column, ColumnShape, SortOrder } from "react-base-table"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import moment from "moment"

import {
  faCompressAlt,
  faPen,
  faFilter,
  faSquareCaretDown,
  faUpRightAndDownLeftFromCenter,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { Button, ContextMenu, FormLabel, Loading, Modal, Pagination, TextInput } from "@wfp/ui"

import { IOutliersData, OutliersFilters } from "../../interfaces/markets"
import { MARKET_MODULE } from "../../constants/constants"
import { defaultOutlierFilters } from "../../constants/validation"

import { useGetMarketsQuery } from "../../services/core/markets"
import { useGetCommodityByCountryQuery } from "../../services/core/commodities"
import { useGetPriceTypeQuery } from "../../services/core/priceType"
import { useLazyGetOutliersListQuery } from "../../services/marketprices/outliers"
import { useEditOutliersMutation } from "../../services/marketprices/commodityPrice"
import { useAppSelector } from "../../hooks/hooksStore"
import useCommon from "../../hooks/useCommon"
import { selectUser } from "../../features/common/commonSlice"
import { checkAccess, formatDate } from "../../utils/helpers"

import CountrySelector from "../../components/Shared/HeaderFilters/AssociatedCountriesSelector"
import Slider from "../../components/Shared/Slider/Slider"
import ContextItem from "../../components/Shared/ContextItem/ContextItem"

const initialFilters: OutliersFilters = {
  adm0Code: 0,
  monthlyThreshold: defaultOutlierFilters?.MoM,
  yearlyThreshold: defaultOutlierFilters?.YoY,
  startDate: null,
  endDate: null,
  skip: 0,
  take: 20,
}

const permissions = {
  moduleId: MARKET_MODULE,
  subModule: "outliers",
}

const OutliersPage = (): ReactElement => {
  const { showSnackBar } = useCommon()
  const user = useAppSelector(selectUser)

  const [filters, setFilters] = useState<OutliersFilters>(initialFilters)
  const [focusedInput, setFocusedInput] = useState<"startDate" | "endDate" | null>(null)
  const [fullscreen, setFullscreen] = useState<boolean>(false)
  const [currentData, setCurrentData] = useState<{ type: "details" | "edit"; data: IOutliersData } | null>(null)
  const [sortBy, setSortBy] = useState<{ key: string; order: SortOrder }>({
    key: "marketName",
    order: "asc",
  })

  const { data: markets, isFetching: isLoadingMarkets } = useGetMarketsQuery(
    { adm0code: filters.adm0Code, skip: 0, top: 0 },
    { skip: !filters.adm0Code, refetchOnMountOrArgChange: true },
  )
  const { data: commodities, isFetching: isLoadingCommodities } = useGetCommodityByCountryQuery(
    { admCode: filters.adm0Code },
    { skip: !filters.adm0Code },
  )
  const { data: priceType, isFetching: isLoadingPriceTypes } = useGetPriceTypeQuery()
  const [getOutliers, { data: outliers, isFetching: isLoadingOutliers }] = useLazyGetOutliersListQuery()
  const [editOutliers] = useEditOutliersMutation()

  const handleColumnSort = (sortByInfo: { key: string; order: SortOrder }): void => {
    setSortBy(sortByInfo)
  }

  const sortedData = useMemo((): IOutliersData[] => {
    if (!sortBy.key) return outliers?.items ?? []

    const sorted = [...(outliers?.items ?? [])].sort((a, b) => {
      const aValue = a[sortBy.key as keyof IOutliersData]
      const bValue = b[sortBy.key as keyof IOutliersData]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue)
      }

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    })

    return sortBy.order === "desc" ? sorted.reverse() : sorted
  }, [outliers, sortBy])

  const onSubmit = async (updatedFilters: OutliersFilters): Promise<void> => {
    if (!filters.adm0Code || !updatedFilters.startDate || !updatedFilters.endDate) return

    try {
      await getOutliers(updatedFilters).unwrap()
    } catch (error) {
      const err = error as FetchBaseQueryError
      showSnackBar((err?.data as { message: string })?.message ?? "Failed to fetch outliers", "error")
    }
  }

  const handleReset = (): void => {
    setFilters(initialFilters)
  }

  const handleApply = async (): Promise<void> => {
    const updatedFilters = { ...filters, skip: 0 }
    setFilters(updatedFilters)
    await onSubmit(updatedFilters)
  }

  const onEdit = async (item: IOutliersData): Promise<void> => {
    try {
      await editOutliers({
        commodityPriceId: item.commodityPriceId,
        commodityPriceValue: item.commodityPriceValue,
        commodityPriceMetadata: item.commodityPriceMetadata,
      }).unwrap()
      setCurrentData(null)
    } catch (error) {
      const err = error as FetchBaseQueryError
      showSnackBar((err?.data as { message: string })?.message ?? "Failed to edit outliers", "error")
    }
  }

  const handleMetadataChange = (index: number, newValue: string): void => {
    setCurrentData(prev => {
      if (!prev) return prev

      const metadataArray = JSON.parse(prev.data.commodityPriceMetadata || "[]")
      const updatedMetadata = metadataArray.map((meta, i) =>
        i === index ? { ...meta, obsPriceValue: Number(newValue) || 0 } : meta,
      )

      const avg = updatedMetadata.reduce((total, meta) => total + meta.obsPriceValue, 0) / updatedMetadata.length

      return {
        ...prev,
        data: {
          ...prev.data,
          commodityPriceMetadata: JSON.stringify(updatedMetadata),
          commodityPriceValue: Math.round(avg),
        },
      }
    })
  }

  useEffect(() => {
    onSubmit(filters)
  }, [filters.skip, filters.take])

  const columns: ColumnShape<IOutliersData>[] = [
    {
      key: "marketName",
      dataKey: "marketName",
      title: "Market",
      sortable: true,
      resizable: true,
      width: 200,
      flexGrow: 1,
    },
    {
      key: "commodityPriceDate",
      dataKey: "commodityPriceDate",
      title: "Date",
      width: 160,
      sortable: true,
      resizable: true,
      cellRenderer: ({ rowData }) => formatDate(rowData.commodityPriceDate, "DD-MMM-YYYY"),
    },
    {
      key: "commodityName",
      dataKey: "commodityName",
      title: "Commodity",
      resizable: true,
      sortable: true,
      width: 200,
    },
    {
      key: "commodityUnitName",
      dataKey: "commodityUnitName",
      title: "Unit",
      resizable: true,
      sortable: true,
      width: 100,
    },
    {
      key: "priceTypeName",
      dataKey: "priceTypeName",
      title: "Price Type",
      resizable: true,
      sortable: true,
      width: 200,
    },
    {
      key: "currencyName",
      dataKey: "currencyName",
      title: "Currency",
      width: 100,
      resizable: true,
      sortable: true,
    },
    {
      key: "commodityPriceValue",
      dataKey: "commodityPriceValue",
      title: "Price",
      width: 200,
      resizable: true,
      sortable: true,
      className: ({ rowData }) => (rowData.isQuartileOutlier ? "bg-yellow-100 text-yellow-800 font-bold" : ""),
      cellRenderer: ({ rowData }) => rowData.commodityPriceValue?.toLocaleString()?.replace(/\.00$/, ""),
    },
    {
      key: "moMpercentage",
      dataKey: "moMpercentage",
      title: "MoM",
      sortable: true,
      width: 100,
      cellRenderer: ({ rowData }) => `${rowData.moMpercentage?.toFixed(2)?.replace(/\.00$/, "")}%`,
    },
    {
      key: "yoYpercentage",
      dataKey: "yoYpercentage",
      title: "YoY",
      sortable: true,
      width: 100,
      cellRenderer: ({ rowData }) => `${rowData.yoYpercentage?.toFixed(2)?.replace(/\.00$/, "")}%`,
    },
    {
      key: "lastUpdateDate",
      dataKey: "lastUpdateDate",
      title: "Last Update",
      width: 160,
      sortable: true,
      resizable: true,
      cellRenderer: ({ rowData }) => formatDate(rowData.lastUpdateDate, "DD-MMM-YYYY"),
    },
    {
      key: "action",
      width: 60,
      align: Column.Alignment.CENTER,
      maxWidth: 60,
      hidden: !checkAccess(user?.roles, [MARKET_MODULE], "outliers", "edit"),
      flexGrow: 0,
      cellRenderer: ({ rowData }) => (
        <div
          aria-hidden
          className="d-flex align-items-center justify-end"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={
              <ContextItem
                permissions={permissions}
                onAction={() => setCurrentData({ type: "edit", data: rowData })}
                feature="edit"
                label="Edit"
                icon={faPen}
                color="var(--support-02)"
              />
            }
          >
            <div className="cursor-pointer">
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-6 border-solid">
        <div className="flex items-center gap-3 mb-2 border-b border-gray-100">
          <div className="bg-[var(--interactive-01)] rounded-lg p-1.5">
            <FontAwesomeIcon icon={faFilter} className="text-white" size="xs" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Outliers Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <FormLabel htmlFor="country" className="required">
              Country
            </FormLabel>
            <CountrySelector
              allowAll={false}
              onSelect={country =>
                setFilters({ ...filters, adm0Code: country?.value, commodityId: undefined, marketId: undefined })
              }
              value={filters.adm0Code}
              className="w-full"
            />
          </div>

          <div>
            <FormLabel htmlFor="market">Market</FormLabel>
            <Select
              isSearchable
              isClearable
              isDisabled={!filters.adm0Code}
              isLoading={isLoadingMarkets}
              placeholder="Select market"
              classNamePrefix="select"
              options={markets?.items ?? []}
              getOptionLabel={option => option.marketName}
              getOptionValue={option => option.marketID.toString()}
              onChange={item => setFilters({ ...filters, marketId: item?.marketID })}
              value={markets?.items.find(m => m.marketID === filters.marketId) ?? null}
            />
          </div>

          <div>
            <FormLabel htmlFor="commodity">Commodity</FormLabel>
            <Select
              isSearchable
              isClearable
              isDisabled={!filters.adm0Code}
              isLoading={isLoadingCommodities}
              placeholder="Select commodity"
              classNamePrefix="select"
              options={commodities ?? []}
              getOptionLabel={option => option.commodityName}
              getOptionValue={option => option.commodityId.toString()}
              onChange={item => setFilters({ ...filters, commodityId: item?.commodityId })}
              value={commodities?.find(c => c.commodityId === filters.commodityId) ?? null}
            />
          </div>

          <div>
            <FormLabel htmlFor="priceType">Price Type</FormLabel>
            <Select
              isSearchable
              isClearable
              isLoading={isLoadingPriceTypes}
              placeholder="Select price type"
              classNamePrefix="select"
              options={priceType ?? []}
              getOptionLabel={option => option.priceTypeName}
              getOptionValue={option => option.priceTypeID.toString()}
              onChange={item => setFilters({ ...filters, priceTypeId: item?.priceTypeID })}
              value={priceType?.find(p => p.priceTypeID === filters.priceTypeId) ?? null}
            />
          </div>

          <div className="2xl:col-span-1 col-span-2">
            <FormLabel htmlFor="dateRange" className="required">
              Date
            </FormLabel>
            <DateRangePicker
              startDate={filters.startDate ? moment(filters.startDate) : null}
              startDateId="startDate"
              startDatePlaceholderText="From"
              endDatePlaceholderText="To"
              endDate={filters.endDate ? moment(filters.endDate) : null}
              endDateId="endDate"
              onDatesChange={({ startDate, endDate }) =>
                setFilters({
                  ...filters,
                  startDate: startDate?.toISOString() || null,
                  endDate: endDate?.toISOString() || null,
                })
              }
              focusedInput={focusedInput}
              onFocusChange={item => setFocusedInput(item)}
              displayFormat="DD/MM/YYYY"
              hideKeyboardShortcutsPanel
              showDefaultInputIcon
              inputIconPosition="after"
              isOutsideRange={() => false}
            />
          </div>

          <div className="flex justify-between col-span-2 2xl:col-span-1 gap-4">
            <div className="w-1/2">
              <FormLabel htmlFor="monthlyThreshold" className="required">
                Monthly Threshold
              </FormLabel>
              <Slider
                value={filters.monthlyThreshold ?? 0}
                onChange={value => setFilters({ ...filters, monthlyThreshold: value })}
                min={0}
                max={100}
                className="pt-3"
                color="var(--interactive-01)"
                defaultValue={defaultOutlierFilters?.MoM}
              />
            </div>

            <div className="w-1/2">
              <FormLabel htmlFor="yearlyThreshold" className="required">
                Yearly Threshold
              </FormLabel>
              <Slider
                value={filters.yearlyThreshold ?? 0}
                className="pt-3"
                onChange={value => setFilters({ ...filters, yearlyThreshold: value })}
                min={0}
                max={100}
                color="var(--interactive-01)"
                defaultValue={defaultOutlierFilters?.YoY}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-1">
          <Button kind="ghost" onClick={handleReset} small>
            Reset Filters
          </Button>
          <Button onClick={handleApply} small disabled={!filters.adm0Code || !(filters.startDate && filters.endDate)}>
            Apply Filters
          </Button>
        </div>
      </div>

      {!!outliers && (
        <div
          className={fullscreen ? "!absolute top-0 left-0 w-full h-full bg-white p-8" : "h-full w-full"}
          style={{ height: fullscreen ? "100vh" : "100%", zIndex: fullscreen ? 10001 : 1 }}
        >
          <div className="flex justify-end mb-1 px-2">
            <FontAwesomeIcon
              icon={fullscreen ? faCompressAlt : faUpRightAndDownLeftFromCenter}
              color="var(--interactive-01)"
              className="ml-1.5 cursor-pointer"
              size="lg"
              title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
              onClick={() => setFullscreen(prev => !prev)}
            />
          </div>

          {isLoadingOutliers ? (
            <div className="w-full h-full flex justify-center items-center">
              <Loading withOverlay={false} small />
            </div>
          ) : (
            <>
              <div
                className="h-full w-full"
                style={{
                  minHeight: "calc(100vh - 500px)",
                  maxHeight: "calc(100vh - 150px)",
                  height: fullscreen ? "calc(100vh - 150px)" : "100%",
                }}
              >
                <AutoSizer>
                  {({ width, height }) => (
                    <BaseTable
                      key={`${sortBy.key}-${sortBy.order}`}
                      data={sortedData}
                      width={width}
                      height={height}
                      rowKey="commodityPriceId"
                      className="custom-table text-sm"
                      rowHeight={38}
                      headerHeight={45}
                      columns={columns}
                      emptyRenderer={() => <p>No records to display</p>}
                      sortBy={sortBy}
                      onColumnSort={item =>
                        handleColumnSort({ key: item.key as keyof IOutliersData, order: item.order })
                      }
                    />
                  )}
                </AutoSizer>
              </div>

              <Pagination
                totalItems={outliers?.count ?? 100}
                pageSize={filters.take}
                pageSizes={[20, 50, 100]}
                page={filters.skip / filters.take + 1}
                onChange={event => {
                  const item = event as unknown as { page: number; pageSize: number }
                  setFilters({ ...filters, skip: (item.page - 1) * item.pageSize, take: item.pageSize })
                }}
              />
            </>
          )}
        </div>
      )}

      <Modal
        open={!!currentData}
        modalHeading="Details"
        lazyLoad
        primaryButtonText="Save"
        secondaryButtonText="Close"
        onRequestSubmit={() => onEdit(currentData?.data as IOutliersData)}
        onRequestClose={() => setCurrentData(null)}
      >
        <div className="flex flex-wrap flex-col gap-2 gap-x-4 w-full px-2 justify-center">
          <p className="text-sm">
            {formatDate(currentData?.data?.commodityPriceDate || "", "DD-MMM-YYYY")} - {currentData?.data?.marketName} -{" "}
            {currentData?.data?.commodityName}
          </p>

          <FormLabel className="mt-2">Price Observations</FormLabel>
          <div className="w-fit min-w-[200px]">
            {JSON.parse(currentData?.data?.commodityPriceMetadata || "[]")?.map((item, index) => (
              <TextInput
                key={index}
                value={item.obsPriceValue ?? 0}
                onChange={e => handleMetadataChange(index, e.target.value)}
                className="custom-text-field mb-1"
                // @ts-expect-error
                type="number"
                min={0}
              />
            ))}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default OutliersPage
